/**
 * Sent in place of a real App Check token when attestation is unavailable.
 *
 * Deliberately a value the server will reject: the request then fails as a
 * logged 401 ("Invalid App Check token") instead of dying in the browser with
 * nothing recorded anywhere. A scan that cannot be attested is going to fail
 * either way — this makes it fail *visibly*.
 */
export const APP_CHECK_UNAVAILABLE = "appcheck-unavailable";

/** How long to wait for reCAPTCHA before giving up on attestation. */
const DEFAULT_TIMEOUT_MS = 5000;

/**
 * Resolve an App Check token, never rejecting and never hanging.
 *
 * reCAPTCHA Enterprise can fail (content blockers, strict privacy settings,
 * captive corporate networks) or simply never answer. Both used to break a
 * scan before the request left the browser.
 */
export async function resolveAppCheckToken(
  fetchToken: () => Promise<{ token: string }>,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<string> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), timeoutMs);
    });
    const result = await Promise.race([fetchToken(), timeout]);
    if (!result) {
      console.error("[appcheck] attestation timed out");
      return APP_CHECK_UNAVAILABLE;
    }
    return result.token;
  } catch (err) {
    console.error("[appcheck] attestation failed", err);
    return APP_CHECK_UNAVAILABLE;
  } finally {
    clearTimeout(timer);
  }
}
